import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { LoadingService } from '../../services/loading.service';

/**
 * Global Loading Overlay Component
 *
 * Displays a semi-transparent overlay with a spinner when HTTP requests are in progress.
 * Automatically responds to the LoadingService state.
 *
 * Add this component once in app.component.ts template.
 */
@Component({
  selector: 'app-global-loading-overlay',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule],
  template: `
    @if (isLoading()) {
      <div class="global-loading-overlay" role="status" aria-live="polite">
        <div class="loading-content">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
            styleClass="global-spinner"
          />
        </div>
      </div>
    }
  `,
  styles: [
    `
      .global-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9998; /* Below startup overlay (9999) but above everything else */
        animation: fadeIn 0.15s ease-out;
        pointer-events: all; /* Prevent interaction with underlying elements */
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 2rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
      }

      :host ::ng-deep .global-spinner .p-progress-spinner-circle {
        stroke: #0d4e6a;
      }
    `,
  ],
})
export class GlobalLoadingOverlayComponent {
  private loadingService = inject(LoadingService);

  // Reactive binding to loading state
  isLoading = computed(() => this.loadingService.isLoading());
}
